import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { useState } from "react";
import { z } from "zod";
import { IconAlertTriangle, IconLock } from "@tabler/icons-react";
import { Alert, Button, Card, Heading, Input, Stack, Text } from "@/components/ui";
import { toastError, toastExito } from "@/lib/toast";
import { resetPassword } from "@/shared/api/auth.api";
import type { ApiProblem } from "@/lib/types";

const schema = z
  .object({
    password: z.string().min(8, "Mínimo 8 caracteres"),
    confirmar: z.string(),
  })
  .refine((v) => v.password === v.confirmar, {
    path: ["confirmar"],
    message: "Las contraseñas no coinciden",
  });

type FormValues = z.infer<typeof schema>;
type FormErrors = Partial<Record<keyof FormValues, string>>;

/**
 * `/restablecer-contrasena?email=…&token=…` (pública) · destino del enlace
 * del correo de recuperación. Valida el token en el backend al enviar la
 * nueva contraseña (un token inválido/expirado da un error claro).
 */
function RestablecerContrasenaPage() {
  const { email, token } = Route.useSearch();
  const navigate = useNavigate();
  const enlaceValido = Boolean(email && token);

  const [values, setValues] = useState<FormValues>({ password: "", confirmar: "" });
  const [errors, setErrors] = useState<FormErrors>({});

  const restablecer = useMutation({
    mutationFn: (newPassword: string) =>
      resetPassword({ email: email!, token: token!, newPassword }),
    onSuccess: () => {
      toastExito({ title: "Contraseña restablecida", message: "Ya puedes iniciar sesión con tu nueva contraseña." });
      void navigate({ to: "/login", replace: true });
    },
    onError: (err: unknown) => {
      const detail = axios.isAxiosError<ApiProblem>(err) ? err.response?.data?.detail : undefined;
      toastError({ title: "No se pudo restablecer", message: detail ?? "Intenta solicitar un enlace nuevo." });
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = schema.safeParse(values);
    if (!parsed.success) {
      const fieldErrors: FormErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof FormValues;
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    restablecer.mutate(parsed.data.password);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card padding="xl" className="w-full max-w-110">
        <Stack gap="md">
          <div>
            <Heading order={2}>Restablecer contraseña</Heading>
            {enlaceValido && (
              <Text size="sm" c="dimmed" className="mt-1">
                Define la nueva contraseña para <b>{email}</b>.
              </Text>
            )}
          </div>

          {!enlaceValido ? (
            <Alert variant="warning" icon={<IconAlertTriangle size={18} />}>
              El enlace está incompleto · abre el enlace tal como llegó en el correo, o{" "}
              <Link to="/recuperar-contrasena" className="underline">
                solicita uno nuevo
              </Link>
              .
            </Alert>
          ) : (
            <form onSubmit={onSubmit} noValidate>
              <Stack gap="sm">
                <Input
                  type="password"
                  label="Nueva contraseña"
                  placeholder="Mínimo 8 caracteres"
                  autoComplete="new-password"
                  leftSection={<IconLock size={16} stroke={1.6} />}
                  size="md"
                  value={values.password}
                  onChange={(e) => setValues((v) => ({ ...v, password: e.target.value }))}
                  error={errors.password}
                />
                <Input
                  type="password"
                  label="Confirmar contraseña"
                  placeholder="Repite la contraseña"
                  autoComplete="new-password"
                  leftSection={<IconLock size={16} stroke={1.6} />}
                  size="md"
                  value={values.confirmar}
                  onChange={(e) => setValues((v) => ({ ...v, confirmar: e.target.value }))}
                  error={errors.confirmar}
                />
                <Button type="submit" fullWidth loading={restablecer.isPending}>
                  Restablecer contraseña
                </Button>
              </Stack>
            </form>
          )}

          <div className="flex justify-center">
            <Link to="/login" className="text-sm text-multiva-600 hover:underline">
              Volver al inicio de sesión
            </Link>
          </div>
        </Stack>
      </Card>
    </div>
  );
}

const str = (v: unknown): string | undefined =>
  typeof v === "string" && v !== "" ? v : undefined;

export const Route = createFileRoute("/restablecer-contrasena")({
  validateSearch: (search: Record<string, unknown>) => ({
    email: str(search.email),
    token: str(search.token),
  }),
  component: RestablecerContrasenaPage,
});
